import { useFragment } from '@apollo/client';
import { DocSourceContentFragmentDoc } from '@cycle-app/graphql-codegen';
import { ComponentProps } from 'react';
import { twJoin } from 'tailwind-merge';

import { ReadOnlyEditor } from 'src/components/Editor';

import { QuoteFiles } from './QuoteFiles';

type Props = ComponentProps<'div'> & {
  docId: string;
  docSourceId: string;
  showFiles?: boolean;
};

export const QuoteContentPreview = ({
  docId,
  docSourceId,
  showFiles = false,
  className,
  ...props
}: Props) => {
  const content = useFragment({
    fragment: DocSourceContentFragmentDoc,
    from: docSourceId,
  }).data.content ?? '';

  return (
    <div
      className={twJoin(
        'min-h-9 rounded-md bg-grey-100 px-2.5 py-1.5 dark:bg-grey-800/50',
        className,
      )}
      {...props}
    >
      <Content content={content} />

      {showFiles && (
        <QuoteFiles
          docId={docId}
          html={content}
        />
      )}
    </div>
  );
};

const paragraphRegex = /<p.*?<\/p>/g;

const Content = ({ content }: { content: string }) => {
  if (!!content?.startsWith('<')) {
    const paragraphs = content.match(paragraphRegex);
    const firstParagraph = paragraphs?.find(p => p !== '<p class=\"editor-paragraph\"></p>');
    return (
      <div className="quote-content-preview text-body text-primary">
        <ReadOnlyEditor content={firstParagraph} />
      </div>
    );
  }

  return (
    <div className="overflow-anywhere line-clamp-3 text-body text-primary">
      {content}
    </div>
  );
};

export const QuoteContentHighlight = ({ content }: { content: string }) => (
  <div
    className="highlight overflow-anywhere min-h-9 select-text rounded-md bg-grey-100 px-3 py-1.5 text-body text-primary dark:bg-grey-800/50"
    dangerouslySetInnerHTML={{
      __html: `<p>${content}</p>`,
    }}
  />
);
